import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { MenuOpen as MenuIcon, ConfirmationNumberOutlined, ChatBubbleOutlineRounded } from '@mui/icons-material'
import {
  Collapse,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  useMenuStyles,
  useListItemButtonStyles,
  useTriggerStyles,
} from '../../style/common'
import { useSelector } from '../../store'
import { getIsRtl } from '../../store/common/selector'
import { useAdminTheme } from '../../hook/useAdminTheme'
import { markTicketSeen, useSupportAlerts } from '../../hook/useSupportAlerts'
import { SupportToasts } from './SupportToasts'
import h from './HeaderV2.module.css'

/*
  The user dashboard's menu look (espy-dashboard container/main/Menu.tsx):
  outline icons (Tabler icons, MIT) in one stroke style, grey when idle and
  white (dark panel) or brand blue (light panel) when selected.
*/
const ICON_PATHS: Record<string, string[]> = {
  home: ['M5 12l-2 0l9 -9l9 9l-2 0', 'M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7', 'M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6'],
  payments: ['M3 8a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3z', 'M3 10l18 0', 'M7 15l.01 0', 'M11 15l2 0'],
  marketing: ['M18 8a3 3 0 0 1 0 6', 'M10 8v11a1 1 0 0 1 -1 1h-1a1 1 0 0 1 -1 -1v-5', 'M12 8h0l4.524 -3.77a.9 .9 0 0 1 1.476 .692v12.156a.9 .9 0 0 1 -1.476 .692l-4.524 -3.77h-8a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1h8'],
  messages: ['M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6', 'M9 17v1a3 3 0 0 0 6 0v-1'],
  packages: ['M12 3l8 4.5l0 9l-8 4.5l-8 -4.5l0 -9l8 -4.5', 'M12 12l8 -4.5', 'M12 12l0 9', 'M12 12l-8 -4.5', 'M16 5.25l-8 4.5'],
  qr: [
    'M4 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z', 'M7 17l0 .01',
    'M14 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z', 'M7 7l0 .01',
    'M4 15a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z', 'M17 7l0 .01',
    'M14 14l3 0', 'M20 14l0 .01', 'M14 14l0 3', 'M14 20l3 0', 'M17 17l3 0', 'M20 17l0 3',
  ],
  users: ['M5 7a4 4 0 1 0 8 0a4 4 0 1 0 -8 0', 'M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2', 'M16 3.13a4 4 0 0 1 0 7.75', 'M21 21v-2a4 4 0 0 0 -3 -3.85'],
  user: ['M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0', 'M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2'],
  admin: ['M6 21v-2a4 4 0 0 1 4 -4h2', 'M22 16c0 4 -2.5 6 -3.5 6s-3.5 -2 -3.5 -6c1 0 2.5 -.5 3.5 -1.5c1 1 2.5 1.5 3.5 1.5z', 'M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0'],
  enterprise: ['M3 21l18 0', 'M9 8l1 0', 'M9 12l1 0', 'M9 16l1 0', 'M14 8l1 0', 'M14 12l1 0', 'M14 16l1 0', 'M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16'],
  apikey: [
    'M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558a2 2 0 0 1 -1.239 .578l-.175 .008h-1.172a1 1 0 0 1 -.993 -.883l-.007 -.117v-1.172a2 2 0 0 1 .467 -1.284l.119 -.13l.414 -.414h2v-2h2v-2l2.144 -2.144l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0z',
    'M15 9h.01',
  ],
  support: ['M8 12a4 4 0 1 0 8 0a4 4 0 1 0 -8 0', 'M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0', 'M15 15l3.35 3.35', 'M9 15l-3.35 3.35', 'M5.65 5.65l3.35 3.35', 'M18.35 5.65l-3.35 3.35'],
  settings: [
    'M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z',
    'M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0',
  ],
  system: ['M3 7a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v2a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3z', 'M3 15a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v2a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3z', 'M7 8l0 .01', 'M7 16l0 .01'],
  chevron: ['M6 9l6 6l6 -6'],
}

const ICON_COLORS = {
  dark: { idle: '#A9B2CC', active: '#FFFFFF' },
  light: { idle: '#64748B', active: '#2D89F4' },
}

function OutlineIcon({ name, color, size = 25 }: { name: string; color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {(ICON_PATHS[name] || []).map((d) => <path key={d} d={d} />)}
    </svg>
  )
}

type Props = {
  navbarOpen: boolean
  setNavbarOpen: (status: boolean) => void
}

interface ChildrenMenuItem {
  label: string
  url: string
  icon: JSX.Element
  iconActive: JSX.Element
}

interface MenuItem {
  label: string
  icon: JSX.Element
  iconActive: JSX.Element
  url: string
  children?: ChildrenMenuItem[]
}

export function Menu({ navbarOpen, setNavbarOpen }: Props) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const menuClasses = useMenuStyles()
  const listItemButtonClasses = useListItemButtonStyles()
  const triggerClasses = useTriggerStyles()

  const isRtl = useSelector(getIsRtl)

  const { theme: menuTheme } = useAdminTheme()
  // unseen support alerts — new tickets, customers' comments (SupportToasts pops each one up)
  const support = useSupportAlerts()
  const colors = ICON_COLORS[menuTheme === 'dark' ? 'dark' : 'light']
  const icon = (name: string, size?: number) => ({
    icon: <OutlineIcon name={name} color={colors.idle} size={size} />,
    iconActive: <OutlineIcon name={name} color={colors.active} size={size} />,
  })

  const data1: MenuItem[] = [
    { label: t('menu.dashboard'), ...icon('home'), url: '/' },
    { label: t('menu.payments'), ...icon('payments'), url: '/payment' },
    { label: t('menu.marketing'), ...icon('marketing'), url: '/marketing' },
    { label: t('menu.messages', 'Messages'), ...icon('messages'), url: '/messages_v2' },
    { label: t('menu.packages'), ...icon('packages'), url: '/package' },
    { label: t('menu.qrRegistration'), ...icon('qr'), url: '/qr-registration' },
    {
      label: t('menu.users'),
      ...icon('users'),
      url: '/users',
      children: [
        { label: t('menu.subscribers'), url: '/subscribers', ...icon('user', 22) },
        { label: t('menu.users'), url: '/admins', ...icon('admin', 22) },
        { label: t('menu.enterprise'), url: '/enterprise', ...icon('enterprise', 22) },
      ],
    },
  ]
  const data2: MenuItem[] = [
    { label: t('menu.apikey'), ...icon('apikey'), url: '/apikey' },
    { label: t('menu.support'), ...icon('support'), url: '/support' },
    { label: t('menu.settings'), ...icon('settings'), url: '/settings' },
    { label: t('menu.system'), ...icon('system'), url: '/system' },
  ]

  /*
    A v2 page is the same menu item as its classic page: /marketing_v2 is
    Marketing, /dashboard_v2 is Dashboard (/), /users/subscribers_v2 is
    Users → Subscribers. Both the address and the items' urls are compared
    without the suffix — the items keep their classic urls, except Messages,
    which only has /messages_v2.
  */
  const plain = (segment?: string) => {
    if (!segment) return ''
    const s = segment.replace(/_v2$/, '')
    return s === 'dashboard' ? '' : s
  }
  const itemKey = (url: string) => '/' + plain(url.split('/')[1])
  const childKey = (url: string) => '/' + plain(url.replace(/^\//, ''))

  const initMenu = React.useMemo(() => {
    return '/' + plain(location.pathname.split('/')[1])
  }, [location.pathname])
  const initSubmenu = React.useMemo(() => {
    const urlPart = location.pathname.split('/')[2]
    const url = urlPart ? '/' + plain(urlPart) : undefined
    return { open: Boolean(url), url: url }
  }, [location.pathname])

  const [menu, setMenu] = React.useState(initMenu)
  const [submenu, setSubmenu] = React.useState(initSubmenu)
  // follow the address however it changed — a link, Back, the Classic/v2 switch — not only a click here
  React.useEffect(() => { setMenu(initMenu) }, [initMenu])
  React.useEffect(() => { setSubmenu(initSubmenu) }, [initSubmenu])
  const menuIs = (url: string) => menu === itemKey(url)
  const submenuIs = (url: string) => submenu.url === childKey(url)

  let navbarClassName = navbarOpen ? 'active' : ''
  navbarClassName = `${navbarClassName} ${isRtl ? 'rtl' : 'ltr'} ${menuTheme === 'dark' ? 'dark' : 'light'}`
  const logoClassName = navbarOpen
    ? 'menuHeader__logo'
    : 'menuHeader__logo_inactive'
  const rtlClassName = isRtl ? 'rtl' : 'ltr'

  const handleMenuClick = (
    e: React.MouseEvent<HTMLDivElement>,
    el: MenuItem,
  ) => {
    e.stopPropagation()

    setMenu(itemKey(el.url))

    if (el.children === undefined) {
      navigate(el.url)
      setSubmenu(initSubmenu)
    } else {
      setSubmenu((state) => ({ ...state, open: !state.open }))
    }
  }

  const handleSubmenuClick = (menuUrl: string, submenuUrl: string) => {
    navigate(menuUrl + submenuUrl)
    setSubmenu({ open: true, url: childKey(submenuUrl) })
  }

  React.useEffect(() => {
    //console.log(menu)
    //console.log(submenu)
  }, [menu, submenu])
  return (
    <Drawer
      classes={menuClasses}
      className={navbarClassName}
      anchor={isRtl ? 'right' : 'left'}
      onClose={() => setNavbarOpen(false)}
      variant="permanent"
    >
      <Grid
        className="menuHeader"
        container
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <div onClick={() => navigate('/')} className={logoClassName} />
        </Grid>
        <Grid item>
          <IconButton
            className={rtlClassName}
            classes={triggerClasses}
            onClick={() => setNavbarOpen(!navbarOpen)}
          >
            <MenuIcon style={{ transform: navbarOpen ? '' : 'scaleX(-1)' }} />
          </IconButton>
        </Grid>
      </Grid>

      <Divider />

      <List>
        {data1.map((el) => (
          <div key={el.url}>
            <ListItemButton
              className={rtlClassName}
              classes={listItemButtonClasses}
              onClick={(event) => handleMenuClick(event, el)}
              selected={
                (menuIs(el.url) && !submenu.open) ||
                (submenu.url === undefined && menuIs(el.url))
              }
            >
              <ListItemIcon>
                {menuIs(el.url) ? el.iconActive : el.icon}
              </ListItemIcon>
              <ListItemText primary={el.label} />
              {el.children && (
                <span style={{ display: 'flex', transition: 'transform .2s', transform: submenu.open ? 'rotate(180deg)' : 'none' }}>
                  <OutlineIcon name="chevron" color={colors.idle} size={18} />
                </span>
              )}
            </ListItemButton>

            {el.children && (
              <Collapse in={submenu.open} timeout="auto" unmountOnExit>
                <List
                  className="menuSub"
                  component="div"
                  disablePadding
                  // sub-items sit indented under their parent while the menu is open
                  sx={navbarOpen ? { '& .MuiListItemButton-root': { ml: isRtl ? '10px' : '24px', mr: isRtl ? '24px' : '10px' } } : undefined}
                >
                  {el.children.map((childEl) => {
                    const isSubmenuActive = submenuIs(childEl.url)
                    return (
                      <ListItemButton
                        key={childEl.url}
                        className={rtlClassName}
                        classes={listItemButtonClasses}
                        onClick={() => handleSubmenuClick(el.url, childEl.url)}
                        selected={submenuIs(childEl.url)}
                      >
                        <ListItemIcon>
                          {isSubmenuActive ? childEl.iconActive : childEl.icon}
                        </ListItemIcon>
                        <ListItemText primary={childEl.label} />
                      </ListItemButton>
                    )
                  })}
                </List>
              </Collapse>
            )}
          </div>
        ))}
      </List>

      <Divider />

      <List>
        {data2.map((el) => (
          <ListItemButton
            key={el.url}
            className={rtlClassName}
            classes={listItemButtonClasses}
            onClick={(event) => {
              // opening Support sees every new ticket and comment — the counts go
              if (el.url === '/support' && (support.tickets > 0 || support.comments > 0)) void markTicketSeen()
              handleMenuClick(event, el)
            }}
            selected={menuIs(el.url)}
          >
            <ListItemIcon sx={{ position: 'relative' }}>
              {menuIs(el.url) ? el.iconActive : el.icon}
              {el.url === '/support' && !navbarOpen && (support.tickets > 0 || support.comments > 0) && <i className={h.menuDot} />}
            </ListItemIcon>
            <ListItemText primary={el.label} />
            {el.url === '/support' && navbarOpen && (support.tickets > 0 || support.comments > 0) && (
              <span className={h.menuCounts}>
                {support.tickets > 0 && (
                  <span className={`${h.menuCount} ${h.menuCountTicket}`} title={`${support.tickets} new ticket${support.tickets === 1 ? '' : 's'}`}>
                    <ConfirmationNumberOutlined sx={{ fontSize: 13 }} />{support.tickets > 9 ? '9+' : support.tickets}
                  </span>
                )}
                {support.comments > 0 && (
                  <span className={h.menuCount} title={`${support.comments} new comment${support.comments === 1 ? '' : 's'}`}>
                    <ChatBubbleOutlineRounded sx={{ fontSize: 12 }} />{support.comments > 9 ? '9+' : support.comments}
                  </span>
                )}
              </span>
            )}
          </ListItemButton>
        ))}
      </List>
      <SupportToasts />
    </Drawer>
  )
}
